import React from 'react'
import { useField } from 'formik'
import Checkbox from '../../Checkbox/Checkbox'
import { modeTypes } from '../constants'
import SelectInlineFooter from './SelectInlineFooter'
import {
  Choice,
  getChoiceLabel,
  withDefaultsFirst,
  SELECT_FIELD_INLINE_CHOICE_LIMIT
} from './selectFieldHelpers'

interface CheckboxFieldProps {
  field: any
  mode?: string
}

/**
 * Multi-select rendered as inline checkboxes (asset-form `display_type = CHECKBOX`).
 * The stored value is an array of the selected choice `value`s; there is no None option.
 */
const CheckboxField: React.FC<CheckboxFieldProps> = ({ field, mode }) => {
  const { formFieldUid } = field
  const [inputField, , helpers] = useField(formFieldUid)
  const values: string[] = Array.isArray(inputField.value) ? inputField.value : []
  const isReadOnly = mode === modeTypes.readOnly || field.readOnly

  const isKeyValue = !!field?.field_metadata?.is_key_value
  const choices: Choice[] = withDefaultsFirst(field?.field_metadata?.choices || [])
  const overLimit = choices.length > SELECT_FIELD_INLINE_CHOICE_LIMIT
  const inlineChoices = overLimit ? choices.slice(0, SELECT_FIELD_INLINE_CHOICE_LIMIT) : choices

  const toggle = (value: string) => {
    const next = values.includes(value) ? values.filter((v) => v !== value) : [...values, value]
    helpers.setValue(next)
  }

  return (
    <div
      className="asset-details-v2-fields-container__select-field"
      data-test-id={`cs-asset-details-field-${formFieldUid}-wrapper`}
    >
      {inlineChoices.map((choice) => (
        <Checkbox
          key={choice.value}
          name={formFieldUid}
          checkboxDisplayType="block"
          value={choice.value}
          checked={values.includes(choice.value)}
          disabled={isReadOnly}
          onChange={() => toggle(choice.value)}
          label={getChoiceLabel(choice, isKeyValue)}
          testId={`cs-asset-details-field-${formFieldUid}-checkbox-${choice.value}`}
        />
      ))}
      <SelectInlineFooter
        fieldUid={formFieldUid}
        choices={choices}
        isKeyValue={isKeyValue}
        isMultiple
        currentValue={values}
        isReadOnly={isReadOnly}
        hasValue={values.length > 0}
        title={field?.title}
        onApply={(v) => helpers.setValue(v)}
        onClear={() => helpers.setValue([])}
      />
    </div>
  )
}

export default CheckboxField
